<template>
  <component :is="layout">
    <QuickValuationTabs />
    <div class="flex-1 bg-linear-to-b from-slate-50 via-white to-slate-50">
      <!-- Hero Section -->
      <div class="pt-12 pb-10 px-4 sm:pt-16 sm:pb-12">
        <div class="max-w-2xl mx-auto text-center">
          <h1 class="text-3xl sm:text-4xl font-light text-slate-900 tracking-tight mb-3">
            {{ $t('messages.quickValuation.title') }}
          </h1>
          <p class="text-base sm:text-lg text-slate-600 font-light leading-relaxed">
            {{ $t('messages.quickValuation.subtitle') }}
          </p>
        </div>
      </div>

      <!-- Main Content -->
      <div class="max-w-2xl mx-auto px-4 pb-20 sm:pb-24">
        <!-- Progress Stepper -->
        <div class="mb-10 sm:mb-12">
          <div class="flex items-center justify-between max-w-md mx-auto relative">
            <!-- Background Lines -->
            <div class="absolute top-[18px] left-0 w-full h-0.5 bg-slate-100 -z-10"></div>
            <div
              class="absolute top-[18px] left-0 h-0.5 bg-blue-500 transition-all duration-500 -z-10"
              :style="{ width: `${((currentStep - 1) / 2) * 100}%` }"
            ></div>

            <div v-for="step in [
              { id: 1, label: $t('messages.quickValuation.stepAddress') },
              { id: 2, label: $t('messages.quickValuation.stepApartment') },
              { id: 3, label: $t('messages.quickValuation.stepDescription') }
            ]" :key="step.id" class="flex flex-col items-center">
              <div
                class="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 border-2"
                :class="[
                  currentStep >= step.id
                    ? 'bg-blue-500 border-blue-500 text-white shadow-md shadow-blue-500/20'
                    : 'bg-white border-slate-200 text-slate-400'
                ]"
              >
                <span v-if="currentStep > step.id" class="pi pi-check text-xs"></span>
                <span v-else>{{ step.id }}</span>
              </div>
              <span
                class="mt-2 text-[10px] sm:text-xs font-semibold uppercase tracking-wider transition-colors duration-300"
                :class="currentStep >= step.id ? 'text-slate-900' : 'text-slate-400'"
              >
                {{ step.label }}
              </span>
            </div>
          </div>
        </div>

        <!-- Step 1: Address Search -->
        <div class="mb-6 sm:mb-8">
          <div class="bg-white rounded-2xl shadow-xs border border-slate-200/60 p-6 sm:p-8 transition-all duration-300 hover:shadow-md flex flex-col gap-4"
               :class="{ 'ring-2 ring-blue-500/30 shadow-md': currentStep === 1 }">
            <div class="flex items-center gap-3 mb-1">
              <div class="w-9 h-9 rounded-full bg-linear-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center text-sm font-semibold shadow-xs"
                   :class="{ 'ring-4 ring-blue-500/20': currentStep === 1 }">
                1
              </div>
              <h2 class="text-lg sm:text-xl font-semibold text-slate-900">
                {{ $t('messages.quickValuation.searchPlaceholder') }}
              </h2>
            </div>

            <!-- Address/Cadastre AutoComplete -->
            <AutoComplete
              v-focustrap
              v-model="searchQuery"
              :suggestions="searchSuggestions"
              :placeholder="$t('messages.quickValuation.searchExample')"
              option-label="label"
              dropdown
              dropdown-mode="current"
              auto-option-focus
              show-empty-message=""
              :dropdownClass="'hidden'"
              :panelClass="'w-full max-w-lg'"
              :inputClass="'rounded-md'"
              class="w-full"
              :loading="searchLoading"
              :pt="{
                root: { class: 'w-full', 'data-testid': 'qv-address-input' },
                input: {
                  class: 'w-full px-4 py-3.5 text-base sm:text-lg border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 placeholder:text-slate-400'
                }
              }"
              fluid
              @complete="onSearch"
              @item-select="onAddressSelect"
            >
              <template #option="{ option }">
                <div
                  class="flex grow items-center justify-between py-2.5 px-2 rounded-lg transition-colors"
                  :class="isRegionLocked(option)
                    ? 'opacity-60 cursor-pointer'
                    : 'hover:bg-slate-50'"
                >
                <div class="flex items-center gap-3">
                  <span
                    class="pi text-lg shrink-0"
                    :class="isRegionLocked(option)
                      ? 'pi-lock text-amber-500'
                      : option.type === 'address'
                        ? 'pi-map-marker text-blue-500'
                        : 'pi-hashtag text-emerald-500'"
                  ></span>
                  <div class="flex-1 min-w-0">
                    <div
                      class="font-medium truncate"
                      :class="isRegionLocked(option) ? 'text-slate-500' : 'text-slate-900'"
                    >
                      {{ option.label }}
                    </div>
                    <div v-if="option.sublabel" class="text-sm text-slate-500 truncate mt-0.5">
                      {{ option.sublabel }}
                    </div>
                  </div>
                </div>
                  <!-- Premium badge for locked regions -->
                  <span
                    v-if="isRegionLocked(option)"
                    class="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
                  >
                    Pro
                  </span>
                </div>
              </template>
              <template #empty>
                <div class="py-2 text-center text-sm text-slate-500">
                  {{ searchLoading ? $t('messages.quickValuation.searching') : $t('messages.quickValuation.noResults') }}
                </div>
              </template>
            </AutoComplete>

            <div v-if="!selectedAddress" class="flex items-center justify-between text-sm min-h-[28px]">
              <div v-if="searchLoading" class="flex items-center gap-2 text-blue-600">
                <span class="pi pi-spin pi-spinner text-blue-500"></span>
                <span class="font-medium">{{ $t('messages.quickValuation.searching') }}</span>
              </div>
              <div v-else-if="searchError" class="flex items-center gap-2 text-red-600">
                <span class="pi pi-exclamation-circle"></span>
                <span class="font-medium">{{ $t('messages.quickValuation.searchError') }}</span>
              </div>
              <div v-else-if="lastSearchTerm.length >= 2 && searchSuggestions.length === 0" class="text-slate-500">
                <span class="pi pi-info-circle mr-1.5"></span>
                {{ $t('messages.quickValuation.searchEmptyHint') }}
              </div>
            </div>

            <!-- Selected Address Display -->
            <Transition name="slide-fade">
              <div v-if="selectedAddress" class="space-y-4">
                <div class="p-4 bg-linear-to-r from-blue-50 via-blue-50/80 to-slate-50 rounded-xl border-2 border-blue-200 shadow-xs">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3 flex-1 min-w-0">
                      <div class="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center shrink-0 shadow-xs">
                        <span class="pi pi-check text-white text-sm"></span>
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="font-semibold text-slate-900 truncate">{{ selectedAddress.label }}</div>
                        <div v-if="selectedAddress.sublabel" class="text-sm text-slate-600 mt-0.5 truncate">{{ selectedAddress.sublabel }}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="p-2 ml-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 shrink-0"
                      :aria-label="$t('messages.close')"
                      @click="clearSelection"
                    >
                      <span class="pi pi-times text-lg"></span>
                    </button>
                  </div>
                </div>

                <!-- Map Preview -->
                <AddressPreviewMap
                  v-if="mapCoordinates"
                  :coordinates="mapCoordinates.coords"
                  :projection="mapCoordinates.projection"
                />
              </div>
            </Transition>
          </div>
        </div>

        <!-- Loading State: Building Data Fetch -->
        <Transition name="slide-fade">
          <div v-if="selectedAddress && loadingBuildingData && !ehrData" class="mb-6 sm:mb-8" data-testid="qv-loading">
            <div class="bg-white rounded-2xl shadow-xs border border-slate-200/60 p-6 sm:p-8">
              <div class="space-y-6">
                <!-- Skeleton Header -->
                <div class="flex items-center gap-3">
                  <Skeleton shape="circle" size="2.25rem" />
                  <Skeleton width="40%" height="1.5rem" />
                </div>

                <!-- Skeleton Items -->
                <div class="space-y-3">
                  <div v-for="i in 3" :key="i" class="p-4 border border-slate-100 rounded-xl flex items-center justify-between">
                    <div class="flex-1 space-y-2">
                      <Skeleton width="30%" height="1.25rem" />
                      <div class="flex gap-2">
                        <Skeleton width="4rem" height="1.5rem" border-radius="1rem" />
                        <Skeleton width="3rem" height="1.5rem" border-radius="1rem" />
                        <Skeleton width="5rem" height="1.5rem" border-radius="1rem" />
                      </div>
                    </div>
                    <Skeleton width="1.5rem" height="1.5rem" />
                  </div>
                </div>

                <!-- Loading text -->
                <div class="flex flex-col items-center pt-2">
                  <div class="flex items-center gap-2 text-blue-600 mb-2">
                    <span class="pi pi-spin pi-spinner"></span>
                    <span class="text-sm font-semibold">{{ $t('messages.quickValuation.loadingBuildingData') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Transition>

        <!-- Step 2: Building Part Selection (shown after address selected) -->
        <Transition name="slide-fade">
          <div v-if="ehrData && currentStep >= 2" class="mb-6 sm:mb-8">
            <div class="bg-white rounded-2xl shadow-xs border border-slate-200/60 p-6 sm:p-8 transition-all duration-300 hover:shadow-md"
                 :class="{ 'ring-2 ring-blue-500/30 shadow-md': currentStep === 2 }">
              <div class="flex items-center gap-3 mb-5">
                <div class="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold shadow-xs transition-all"
                     :class="currentStep >= 2
                       ? 'bg-linear-to-br from-blue-500 to-blue-600 text-white ring-4 ring-blue-500/20'
                       : 'bg-slate-200 text-slate-500'">
                  2
                </div>
                <h2 class="text-lg sm:text-xl font-semibold text-slate-900">
                  {{ $t('messages.quickValuation.selectBuildingPart') }}
                </h2>
                <p class="text-sm sm:text-base text-slate-600 font-medium">
                  {{ $t('messages.quickValuation.foundLivingRooms', { count: buildingParts.length }) }}
                </p>
              </div>

              <BuildingPartSelector
                :building-parts="buildingParts"
                :selected-building-part="selectedBuildingPart"
                :building-parts-list-expanded="buildingPartsListExpanded"
                @select="selectBuildingPartAndCollapse"
                @toggle-expand="buildingPartsListExpanded = $event"
              />

              <!-- Loading State -->
              <div v-if="loadingBuildingData && buildingParts.length === 0 && !selectedBuildingPart" class="py-12">
                <div class="space-y-4">
                  <div v-for="i in 2" :key="i" class="p-5 border-2 border-slate-100 rounded-xl flex items-center justify-between">
                    <div class="flex-1 space-y-3">
                      <Skeleton width="40%" height="1.5rem" />
                      <div class="flex gap-2">
                        <Skeleton width="4rem" height="1.75rem" border-radius="1rem" />
                        <Skeleton width="4rem" height="1.75rem" border-radius="1rem" />
                        <Skeleton width="4rem" height="1.75rem" border-radius="1rem" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Parts Found -->
              <div v-if="!loadingBuildingData && buildingParts.length === 0 && !selectedBuildingPart" class="text-center py-12 text-slate-500">
                <span class="pi pi-info-circle text-3xl mb-3 text-slate-400"></span>
                <p class="text-base font-medium">{{ $t('messages.quickValuation.noLivingRoomsFound') }}</p>
              </div>
            </div>
          </div>
        </Transition>

        <!-- Step 3: Pivot Fields Form -->
        <Transition name="slide-fade">
          <div v-if="selectedBuildingPart && currentStep >= 3" class="mb-6 sm:mb-8">
            <div class="bg-white rounded-2xl shadow-xs border border-slate-200/60 p-6 sm:p-8 transition-all duration-300 hover:shadow-md"
                 :class="{ 'ring-2 ring-blue-500/30 shadow-md': currentStep === 3 }">
              <div class="flex items-center gap-3 mb-6">
                <div class="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold shadow-xs transition-all"
                     :class="currentStep >= 3
                       ? 'bg-linear-to-br from-blue-500 to-blue-600 text-white ring-4 ring-blue-500/20'
                       : 'bg-slate-200 text-slate-500'">
                  3
                </div>
                <h2 class="text-lg sm:text-xl font-semibold text-slate-900">
                  {{ $t('messages.quickValuation.pivotFieldsTitle') }}
                </h2>
              </div>

              <p class="text-sm sm:text-base text-slate-600 mb-6 leading-relaxed">
                {{ requiredInputs ? $t('messages.quickValuation.pivotHelperRequired') : $t('messages.quickValuation.pivotHelper') }}
              </p>

              <PivotForm
                :pivot-fields="pivotFields"
                :errors="errors"
                :required="requiredInputs"
                @update:pivot-fields="updatePivotFields"
              />
            </div>
          </div>
        </Transition>

        <!-- Calculate Button -->
        <Transition name="slide-fade">
          <div v-if="canCalculate" class="text-center mt-8">
            <div class="sm:static fixed left-0 right-0 bottom-4 sm:bottom-auto px-4 sm:px-0 z-10">
              <Button
                :label="calculating ? $t('messages.quickValuation.calculating') : $t('messages.quickValuation.calculateButton')"
                :loading="calculating"
                :disabled="calculating"
                data-testid="qv-calculate-btn"
                class="w-full sm:w-auto px-10 py-3.5 text-base sm:text-lg font-semibold bg-linear-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:transform-none"
                @click="calculate"
              />
            </div>
          </div>
        </Transition>

        <!-- Error Message -->
        <Transition name="fade">
          <div v-if="error" class="mt-6" data-testid="qv-error-message">
            <Message
              :severity="errorType === 'service_unavailable' ? 'warn' : 'error'"
              :closable="true"
              class="border-2"
              :class="errorType === 'service_unavailable'
                ? 'border-amber-200'
                : 'border-red-300 bg-red-50 text-red-800'"
              @close="error = null; errorType = null"
            >
              <div class="flex items-center gap-2">
                <span
                  class="pi"
                  :class="errorType === 'service_unavailable' ? 'pi-exclamation-circle' : 'pi-exclamation-triangle'"
                ></span>
                <span class="font-semibold">{{ error }}</span>
              </div>
            </Message>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Region Upgrade Dialog -->
    <RegionUpgradeDialog
      v-model:visible="showRegionUpgradeDialog"
      :region-name="lockedRegionName"
    />
  </component>
</template>

<script setup>
import { computed, ref, onBeforeUnmount, watch } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import AppLayout from '@/Layouts/AppLayout.vue'
import FocusedLayout from '@/Layouts/FocusedLayout.vue'
import RatingMultiSelect from '@/components/RatingMultiSelect.vue'
import AddressPreviewMap from '@/components/quick-valuation/AddressPreviewMap.vue'
import BuildingPartSelector from '@/components/quick-valuation/BuildingPartSelector.vue'
import PivotForm from '@/components/quick-valuation/PivotForm.vue'
import QuickValuationTabs from '@/components/quick-valuation/QuickValuationTabs.vue'
import RegionUpgradeDialog from '@/components/quick-valuation/RegionUpgradeDialog.vue'
import { useFeature } from '@/composables/useFeature'

const layout = computed(() => usePage().props.layout === 'FocusedLayout' ? FocusedLayout : AppLayout)

const { t } = useI18n()

const props = defineProps({
  /**
   * The user's servable counties [{name, tase1, mode}] from CountyAccess —
   * entitlements ∩ model coverage ∩ release flag. Drives the search locks and
   * the hero copy; the server enforces the same rule in calculate().
   */
  counties: {
    type: Array,
    default: () => [],
  },
})

// State
const searchQuery = ref('')
const searchSuggestions = ref([])
const searchLoading = ref(false)
const searchError = ref(null)
const lastSearchTerm = ref('')
const debounceTimer = ref(null)
const abortController = ref(null)
const selectedAddress = ref(null)
const ehrData = ref(null)
const selectedBuildingPart = ref(null)
const loadingBuildingData = ref(false)
const calculating = ref(false)
const error = ref(null)
const errorType = ref(null)
const autoSelectedBuildingPart = ref(false)
const buildingPartsListExpanded = ref(true)

const pivotFields = ref({
  ehitise_seisukord: [],
  hooneosa_seisukord: [],
  panipaik: [],
  kinnistu_parkimine: [],
  rodu: [],
})

const errors = ref({})

/**
 * RED-356 (Toomas 16.10): behind `qv-required-inputs` every description field
 * is mandatory, so an unset condition never reaches the model as "missing".
 * Same order as QuickValuationRequest::DESCRIPTION_FIELDS, which enforces it.
 */
const requiredInputs = useFeature('qv-required-inputs')
const DESCRIPTION_FIELDS = ['ehitise_seisukord', 'hooneosa_seisukord', 'panipaik', 'rodu', 'kinnistu_parkimine']

const isEmptyField = (value) => !Array.isArray(value) || value.length === 0

const requiredInputMessage = (field) => t(`messages.quickValuation.requiredInputs.${field}`)

// A field's error clears as soon as it holds a value; nothing the user picked is touched.
const updatePivotFields = (fields) => {
  pivotFields.value = fields
  const remaining = { ...errors.value }
  for (const field of DESCRIPTION_FIELDS) {
    if (!isEmptyField(fields[field])) delete remaining[field]
  }
  errors.value = remaining
}

// Region upgrade dialog state
const showRegionUpgradeDialog = ref(false)
const lockedRegionName = ref('')

// Computed
const currentStep = computed(() => {
  if (!selectedAddress.value) return 1
  if (!selectedBuildingPart.value) return 2
  return 3
})

const buildingParts = computed(() => {
  const parts = ehrData.value?.livingRoom || []
  return [...parts].sort((a, b) => {
    const floorA = Number(a['ehitise-osa-korrus'] || 0)
    const floorB = Number(b['ehitise-osa-korrus'] || 0)
    if (floorA !== floorB) return floorA - floorB
    const numberA = Number(a['ehitise-osa-number'] || 0)
    const numberB = Number(b['ehitise-osa-number'] || 0)
    return numberA - numberB
  })
})

const canCalculate = computed(() => {
  return selectedBuildingPart.value !== null
})

const isUnitAddress = computed(() => {
  const adsOid = selectedAddress.value?.ads_oid || selectedAddress.value?.tunnus
  return typeof adsOid === 'string' && /^ER|^MR/i.test(adsOid)
})

const mapCoordinates = computed(() => {
  if (!selectedAddress.value?.coordinates) return null

  const coords = selectedAddress.value.coordinates

  // If address, it's [lat, lon] -> need [lon, lat] for WGS84
  if (selectedAddress.value.type === 'address') {
    return {
      coords: [coords[1], coords[0]],
      projection: 'EPSG:4326'
    }
  }

  // For cadastre it's already [x, y] in L-EST (EPSG:3301)
  if (selectedAddress.value.type === 'cadastre') {
    return {
      coords: [coords[0], coords[1]],
      projection: 'EPSG:3301'
    }
  }

  return null
})

// Region locking helpers — entitlement-driven (server enforces the same rule)
const enabledTase1 = computed(() => new Set(props.counties.map((c) => c.tase1)))

const isRegionLocked = (option) => {
  // Only check address type (cadastre doesn't have tase1_nimetus in results)
  if (option.type !== 'address') return false
  return Boolean(option.tase1_nimetus) && !enabledTase1.value.has(option.tase1_nimetus)
}

const openUpgradeDialog = (option) => {
  lockedRegionName.value = option.tase1_nimetus || 'selles piirkonnas'
  showRegionUpgradeDialog.value = true
}

// Methods
const performSearch = async (query) => {
  if (!query || query.length < 2) {
    searchSuggestions.value = []
    searchError.value = null
    searchLoading.value = false
    lastSearchTerm.value = query
    return
  }

  searchLoading.value = true
  searchError.value = null
  lastSearchTerm.value = query

  if (abortController.value) {
    abortController.value.abort()
  }
  abortController.value = new AbortController()

  try {
    const response = await axios.get('/api/search/location', {
      params: { query, type: 'building' },
      signal: abortController.value.signal,
    })
    searchSuggestions.value = response.data.results || []
  } catch (err) {
    if (axios.isCancel?.(err)) {
      return
    }
    if (err.name === 'CanceledError') {
      return
    }
    console.error('Search failed:', err)
    searchError.value = t('messages.quickValuation.searchError')
    searchSuggestions.value = []
  } finally {
    searchLoading.value = false
  }
}

const onSearch = async (event) => {
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value)
  }
  debounceTimer.value = setTimeout(() => {
    performSearch(event.query)
  }, 250)
}

const onAddressSelect = async (event) => {
  // Prevent selection of locked regions
  if (isRegionLocked(event.value)) {
    openUpgradeDialog(event.value)
    searchQuery.value = ''
    return
  }

  selectedAddress.value = event.value
  searchQuery.value = event.value.label
  autoSelectedBuildingPart.value = false
  buildingPartsListExpanded.value = true

  await fetchBuildingData()
}

const errorMessageForType = (type) => {
  if (type === 'apartment_not_found') {
    return t('messages.quickValuation.apartmentNotFound')
  }
  if (type === 'ehr_unavailable') {
    return t('messages.quickValuation.ehrUnavailable')
  }
  if (type === 'house_not_supported') {
    return t('messages.quickValuation.houseNotSupported')
  }
  return null
}

const fetchBuildingData = async () => {
  if (!selectedAddress.value) return

  loadingBuildingData.value = true
  error.value = null
  errorType.value = null
  // A new address replaces the previous building: a refused house (RED-355) or a failed
  // fetch must not leave the last building's flats on screen to be valued under this address.
  ehrData.value = null
  selectedBuildingPart.value = null

  try {
    // Determine the search parameter based on address type
    const params = {}

    if (selectedAddress.value.type === 'cadastre') {
      params.cadastre_id = selectedAddress.value.tunnus || selectedAddress.value.label
    } else {
      params.address_id = selectedAddress.value.id
    }

    const response = await axios.post('/api/quick-valuation/building-data', params)

    if (response.data.success) {
      ehrData.value = response.data.data

      // Surface soft warning from backend (e.g. apartment_not_found).
      // Building data still rendered so the user can manually pick a part.
      if (response.data.error_type) {
        errorType.value = response.data.error_type
        error.value =
          errorMessageForType(response.data.error_type)
          || response.data.error
          || t('messages.quickValuation.buildingDataError')
      }

      // Auto-select building part:
      // 1. If apartment address (ER/MR), match by adsOid
      // 2. Use backend suggestion if provided
      // 3. Otherwise if only one part, auto-select it
      const suggestedAdsOid = response.data.suggested_building_part_ads_oid
      const isUnit = isUnitAddress.value
      const parts = buildingParts.value
      const targetAdsOid = suggestedAdsOid || selectedAddress.value.ads_oid

      if (parts.length > 0) {
        let matchingPart = null
        if (targetAdsOid) {
          matchingPart = parts.find((p) => p.adsOid === targetAdsOid)
        }
        if (!matchingPart && isUnit) {
          matchingPart = parts.find((p) => p.adsOid?.startsWith('ER') || p.adsOid?.startsWith('MR'))
        }
        if (!matchingPart && parts.length === 1) {
          matchingPart = parts[0]
        }
        if (matchingPart) {
          selectBuildingPart(matchingPart, true)
          buildingPartsListExpanded.value = false
        }
      }
    } else {
      errorType.value = response.data.error_type || null
      error.value =
        errorMessageForType(response.data.error_type)
        || response.data.error
        || t('messages.quickValuation.buildingDataError')
    }
  } catch (err) {
    console.error('Building data fetch failed:', err)
    const backendType = err.response?.data?.error_type
    errorType.value = backendType || 'ehr_unavailable'
    error.value =
      errorMessageForType(errorType.value)
      || err.response?.data?.error
      || t('messages.quickValuation.buildingDataError')
  } finally {
    loadingBuildingData.value = false
  }
}

const selectBuildingPart = (part, auto = false) => {
  selectedBuildingPart.value = part
  autoSelectedBuildingPart.value = auto
}

const selectBuildingPartAndCollapse = (part) => {
  selectBuildingPart(part, false)
  buildingPartsListExpanded.value = false
}

const clearSelection = () => {
  selectedAddress.value = null
  searchQuery.value = ''
  ehrData.value = null
  selectedBuildingPart.value = null
  pivotFields.value = {
    ehitise_seisukord: [],
    hooneosa_seisukord: [],
    panipaik: [],
    kinnistu_parkimine: [],
    rodu: [],
  }
  errors.value = {}
  autoSelectedBuildingPart.value = false
  buildingPartsListExpanded.value = true
}

const validate = () => {
  errors.value = {}

  if (requiredInputs.value) {
    for (const field of DESCRIPTION_FIELDS) {
      if (isEmptyField(pivotFields.value[field])) {
        errors.value[field] = requiredInputMessage(field)
      }
    }
  }

  return Object.keys(errors.value).length === 0
}

const calculate = async () => {
  if (!validate()) return

  calculating.value = true
  error.value = null
  errorType.value = null

  try {
    const payload = {
      // Send the globally-unique adsOid, NOT the apartment number. A building
      // spanning sub-addresses (Aiandi tn 7 → 7/1, 7/2, 7/3) repeats unit numbers
      // ("14" exists three times), so the number alone resolved to the wrong unit.
      // Fall back to the number only for the rare part with no adsOid.
      building_part_id: selectedBuildingPart.value.adsOid || selectedBuildingPart.value['ehitise-osa-number'],
      ehitise_seisukord: pivotFields.value.ehitise_seisukord,
      hooneosa_seisukord: pivotFields.value.hooneosa_seisukord,
      panipaik: pivotFields.value.panipaik,
      kinnistu_parkimine: pivotFields.value.kinnistu_parkimine,
      rodu: pivotFields.value.rodu,
    }

    // Add address identifiers
    if (selectedAddress.value.type === 'cadastre') {
      payload.cadastre_id = selectedAddress.value.tunnus || selectedAddress.value.label
    } else {
      if (selectedAddress.value.id) {
        payload.address_id = selectedAddress.value.id
      }
      if (selectedAddress.value.ads_oid) {
        payload.ads_oid = selectedAddress.value.ads_oid
      }
    }

    // Add EHR code if we have it (resolveGeneral() emits the lowercase key)
    if (ehrData.value?.general?.ehrkood) {
      payload.ehr_code = ehrData.value.general.ehrkood
    }

    // RED-214: forward cadaster_id derived from EHR so the persisted Evaluation
    // gets the right cadaster when the user picked the property by address.
    // `loadLandBoardForEvaluation` keys on `evaluation.cadaster_id` to fetch the
    // bbox that drives ESG; a null cadaster cascades to "ei paikne…" auto-text
    // in the exported DOCX even when the property sits inside a heat island.
    if (!payload.cadastre_id && ehrData.value?.cadaster?.[0]) {
      payload.cadastre_id = ehrData.value.cadaster[0]
    }

    const response = await axios.post('/api/quick-valuation/calculate', payload)

    if (response.data.success) {
      // Redirect to result page
      router.visit(`/quick-valuation/${response.data.evaluation_id}`)
    } else {
      error.value = response.data.error || t('messages.quickValuation.calculationError')
      errorType.value = response.data.error_type || 'internal_error'
    }
  } catch (err) {
    // RED-356: the server's required-field check (QuickValuationRequest) lands
    // next to its field, worded from et.json, not in the error banner.
    const fieldErrors = err.response?.status === 422 ? err.response.data?.errors ?? {} : {}
    const missing = DESCRIPTION_FIELDS.filter((field) => fieldErrors[field])
    if (missing.length > 0) {
      errors.value = Object.fromEntries(missing.map((field) => [field, requiredInputMessage(field)]))
      return
    }
    console.error('Calculation failed:', err)
    // Server-side county gate (stale search cache / crafted request): show the
    // same upgrade dialog the search lock uses.
    const backendType = err.response?.data?.error_type
    if (backendType === 'county_not_allowed' || backendType === 'county_unresolved') {
      lockedRegionName.value = err.response?.data?.county || selectedAddress.value?.tase1_nimetus || 'selles piirkonnas'
      showRegionUpgradeDialog.value = true
    }
    error.value = err.response?.data?.error || t('messages.quickValuation.calculationError')
    errorType.value = err.response?.data?.error_type || 'internal_error'
  } finally {
    calculating.value = false
  }
}

onBeforeUnmount(() => {
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value)
    debounceTimer.value = null
  }
})

</script>

<style scoped>
  @reference '../../css/tailwind.css';

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.98);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* Smooth focus transitions */
button:focus-visible,
[role="button"]:focus-visible {
  outline: 2px solid var(--color-blue-500);
  outline-offset: 2px;
}
</style>
